// Quick note from any page of the app: the ✎ button (or the N key) opens a small box; the note is
// saved to today's session notes with the time, so it shows on Live session and in the Notebook's
// day timeline. "Entered" and "Exited" notes join the matching trade's notes (sessionLinks.js).
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from './api';
import { useI18n } from './i18n';
import { dayKey } from './pnl';
import { NOTE_TYPES } from './sessionNotes';
import { appendPhrase, MicButton } from './speech';

const typing = (el) => el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));

export default function QuickJot({ onSaved, onSessionExpired }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [type, setType] = useState('thought');
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');
  const box = useRef(null);
  const input = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && open) { setOpen(false); return; }
      if ((e.key === 'n' || e.key === 'N') && !e.metaKey && !e.ctrlKey && !e.altKey && !typing(document.activeElement) && !document.querySelector('.modal-backdrop, [role="dialog"][aria-modal="true"]')) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    setTimeout(() => input.current?.focus(), 30);
    const close = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  async function save(e) {
    e.preventDefault();
    const clean = text.trim();
    if (!clean || state === 'saving') return;
    setState('saving');
    setError('');
    try {
      const result = await api(`/notes/${dayKey(new Date())}/log`, { method: 'POST', body: { type, text: clean } });
      onSaved(result);
      setText('');
      setType('thought');
      setState(result.linkedTrades && result.linkedTrades.length ? 'linked' : 'saved');
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setState('idle');
      setError(err.message);
    }
    return undefined;
  }

  return (
    <div className="quickjot" ref={box}>
      {open && (
        <form className="quickjot-panel" onSubmit={save} role="dialog" aria-label={t('Quick note')}>
          <div className="quickjot-head">
            <strong>{t('Quick note')}</strong>
            <span className="field-hint">{t('Saved to today with the time.')}</span>
          </div>
          <div className="quickjot-types" role="group" aria-label={t('Kind of note')}>
            {NOTE_TYPES.map((n) => (
              <button key={n.id} type="button" className={n.id === type ? 'active' : ''} aria-pressed={n.id === type} onClick={() => setType(n.id)}>
                <span aria-hidden="true">{n.icon}</span> {t(n.label)}
              </button>
            ))}
          </div>
          <textarea ref={input} rows={2} value={text} maxLength={1000} onChange={(e) => { setText(e.target.value); if (state !== 'saving') setState('idle'); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) save(e); }}
            placeholder={type === 'entered' ? t('Why you took it: setup, level, plan…') : type === 'exited' ? t('Why you got out…') : t('What’s on your mind?')}
            aria-label={t('Note')} />
          {(type === 'entered' || type === 'exited') && (
            <p className="field-hint">{t('It will be added to the trade’s notes once the trade is in your journal.')}</p>
          )}
          {error && <p className="error">{error}</p>}
          <div className="quickjot-foot">
            <MicButton onText={(phrase) => { setText((v) => appendPhrase(v, phrase)); setState('idle'); }} />
            <span className="quickjot-status" aria-live="polite">
              {state === 'linked' ? `✓ ${t('Saved and added to the trade.')}` : state === 'saved' ? `✓ ${t('Saved.')}` : ''}
              {(state === 'saved' || state === 'linked') && (
                <> <Link to={`/app/notebook?day=${dayKey(new Date())}`} onClick={() => setOpen(false)}>{t('See today')} &rarr;</Link></>
              )}
            </span>
            <button type="submit" className="small" disabled={!text.trim() || state === 'saving'}>{state === 'saving' ? t('Saving…') : t('Save')}</button>
          </div>
        </form>
      )}
      <button type="button" className="quickjot-button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        aria-label={t('Quick note')} title={`${t('Quick note')} (N)`}>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 20h4L19 9l-4-4L4 16zM13.5 6.5l4 4" />
        </svg>
      </button>
    </div>
  );
}
